/**
 * This exposes functions that will return true when a feature toggle has been set.
 * It will work both on the server and client
 *
 * To enable a feature:
 *
 *  1) Find the feature name in the `Feature` type below, for instance 'MYACCOUNT'
 *  2) Set an env var using the FEATURE_ prefix when starting the server, ie FEATURE_MYACCOUNT
 *  3) Run the app with `FEATURE_MYACCOUNT=true yarn serve` in dev mode or you can add it the .env file
 *
 * To add a feature toggle
 *
 *  1) Add a name to the Feature type, AWESOME_SAUCE for example,
 *      ex: `| 'AWESOME_SAUCE'`
 *  2) Export a function named isAwesomeSauceEnabled
 *      ex: `export const isAwesomeSauceEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'AWESOME_SAUCE')`
 *  3) To enable it see above
 */

import getConfig from 'next/config'

type Feature =
  | 'ABTESTING'
  | 'ADDRESSBOOK'
  | 'MYACCOUNT'
  | 'CONTACTLENS'
  | 'CONTACTLENS_STOCK'
  | 'SOLUTIONS'
  | 'SEARCH'
  | 'GPS_STORES'
  | 'STORES_PLACE_PREDICTIONS'
  | 'MULTILINGUAL'
  | 'VTO'
  | 'CROSS_SELL'
  | 'REVIEWS'

type FeatureCheck = () => boolean

let enabledFeatures: string[] | undefined

const getEnabledFeatures = () => {
  enabledFeatures = getConfig().publicRuntimeConfig.grandvision.features as string[]

  if (typeof window === 'undefined') {
    console.info(`> Feature toggles enabled: ${enabledFeatures.length ? enabledFeatures : '-'}`)
  }
}

const isFeatureEnabled = (feature: Feature): boolean => {
  if (!enabledFeatures) {
    getEnabledFeatures()
  }
  return enabledFeatures?.some((e) => e.toUpperCase() === feature.toUpperCase()) || false
}

export const isABTestingEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ABTESTING')
export const isAddressBookEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'ADDRESSBOOK')
export const isMyAccountEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MYACCOUNT')
export const isContactLensEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CONTACTLENS')
export const isContactLensStockEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'CONTACTLENS_STOCK'
)
export const isSolutionsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SOLUTIONS')
export const isSearchEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'SEARCH')
export const isVtoEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'VTO')
export const isGPSEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'GPS_STORES')
export const isPlacePredictionsEnabled: FeatureCheck = isFeatureEnabled.bind(
  null,
  'STORES_PLACE_PREDICTIONS'
)
export const isMultilingualEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'MULTILINGUAL')
export const isCrossSellEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'CROSS_SELL')
export const isReviewsEnabled: FeatureCheck = isFeatureEnabled.bind(null, 'REVIEWS')
